import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import * as React from 'react';
import { cn } from '@/lib/utils';

const CollapsibleContext = React.createContext({ hasBeenOpened: false });

const Collapsible = React.forwardRef<
  React.ComponentRef<typeof CollapsiblePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Root>
>(({ defaultOpen, onOpenChange, ...props }, ref) => {
  const [hasBeenOpened, setHasBeenOpened] = React.useState(!!defaultOpen);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (open) setHasBeenOpened(true);
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  return (
    <CollapsibleContext.Provider value={{ hasBeenOpened }}>
      <CollapsiblePrimitive.Root
        ref={ref}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </CollapsibleContext.Provider>
  );
});
Collapsible.displayName = 'Collapsible';

const CollapsibleTrigger = CollapsiblePrimitive.CollapsibleTrigger;

const CollapsibleContent = React.forwardRef<
  React.ComponentRef<typeof CollapsiblePrimitive.CollapsibleContent>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleContent>
>(({ className, ...props }, ref) => {
  const { hasBeenOpened } = React.useContext(CollapsibleContext);

  if (!hasBeenOpened) {
    // Never opened: let Radix handle mount/unmount normally (less initial DOM)
    return (
      <CollapsiblePrimitive.CollapsibleContent ref={ref} className={cn(className)} {...props} />
    );
  }

  // Opened at least once: keep mounted, hide with CSS to preserve state
  return (
    <CollapsiblePrimitive.CollapsibleContent
      ref={ref}
      forceMount
      className={cn('data-[state=closed]:hidden', className)}
      {...props}
    />
  );
});
CollapsibleContent.displayName = 'CollapsibleContent';

export { Collapsible, CollapsibleTrigger, CollapsibleContent };
